import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Circle, CircleMarker, Popup } from 'react-leaflet'
import { useConflictEvents } from '../hooks/useQueries'
import { track } from '../umami'
import type { ConflictEventFeature } from '../types'

function escHtml(str: string | undefined): string {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function yearFromISO(dateStr: string): number {
  return new Date(dateStr).getFullYear()
}

interface EventLayerProps {
  event: ConflictEventFeature
}

function EventLayer({ event }: EventLayerProps) {
  const [lat, lon] = [event.geometry.coordinates[1], event.geometry.coordinates[0]]
  const props = event.properties
  const radiusKm = props.affectedRadiusKm || 0.5

  return (
    <>
      <Circle
        center={[lat, lon]}
        radius={radiusKm * 1000}
        pathOptions={{
          color: '#e94560',
          weight: 1,
          fillColor: '#e94560',
          fillOpacity: 0.12,
        }}
        interactive={false}
      />
      <CircleMarker
        center={[lat, lon]}
        radius={8}
        className="pulse-marker"
        pathOptions={{
          color: '#e94560',
          weight: 2,
          fillColor: '#e94560',
          fillOpacity: 0.6,
        }}
        eventHandlers={{
          click: () => {
            track('conflict_event_clicked', {
              event_id: event.id,
              event_title: props.title,
            })
          },
        }}
      >
        <Popup>
          <div className="site-popup">
            <h3>{escHtml(props.title)}</h3>
            <div className="popup-meta">
              {escHtml(props.location)} &middot; {props.date}
            </div>
            <p
              style={{
                marginTop: '0.4rem',
                fontSize: '0.85rem',
                color: '#ccc',
              }}
            >
              {escHtml(props.description)}
            </p>
          </div>
        </Popup>
      </CircleMarker>
    </>
  )
}

/** The overlay layers rendered inside MapContainer */
export function ConflictOverlay() {
  const { data } = useConflictEvents()
  const [enabled, setEnabled] = useState(true)
  const [fromYear, setFromYear] = useState(0)
  const [toYear, setToYear] = useState(9999)
  const initialized = useRef(false)

  const features = data?.features ?? []

  const years = useMemo(() => features.map((f) => yearFromISO(f.properties.date)), [features])

  const minYear = useMemo(() => (years.length ? Math.min(...years) : 2000), [years])
  const maxYear = useMemo(() => (years.length ? Math.max(...years) : 2025), [years])

  // Initialize range once data loads
  useEffect(() => {
    if (features.length && !initialized.current) {
      initialized.current = true
      setFromYear(minYear)
      setToYear(maxYear)
    }
  }, [features.length, minYear, maxYear])

  const visibleEvents = useMemo(() => {
    if (!enabled) return []
    return features.filter((f) => {
      const y = yearFromISO(f.properties.date)
      return y >= fromYear && y <= toYear
    })
  }, [features, enabled, fromYear, toYear])

  // Expose scrubber state to the DOM-based scrubber bar
  useEffect(() => {
    window.__timelineState = {
      enabled,
      setEnabled,
      fromYear,
      toYear,
      setFromYear,
      setToYear,
      minYear,
      maxYear,
    }
    window.dispatchEvent(new Event('timeline-state-update'))
  }, [enabled, fromYear, toYear, minYear, maxYear])

  return (
    <>
      {visibleEvents.map((f) => (
        <EventLayer key={f.id} event={f} />
      ))}
    </>
  )
}

declare global {
  interface Window {
    __timelineState?: {
      enabled: boolean
      setEnabled: (v: boolean) => void
      fromYear: number
      toYear: number
      setFromYear: (v: number) => void
      setToYear: (v: number) => void
      minYear: number
      maxYear: number
    }
  }
}

/** The scrubber bar rendered outside MapContainer */
export default function TimelineScrubber() {
  const [state, setState] = useState<Window['__timelineState']>(undefined)

  useEffect(() => {
    const handler = () => setState(window.__timelineState)
    window.addEventListener('timeline-state-update', handler)
    // Check if already set
    if (window.__timelineState) setState(window.__timelineState)
    return () => window.removeEventListener('timeline-state-update', handler)
  }, [])

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!state) return
      const from = parseInt(e.target.value)
      state.setFromYear(from)
      if (from > state.toYear) state.setToYear(from)
    },
    [state],
  )

  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!state) return
      const to = parseInt(e.target.value)
      state.setToYear(to)
      if (to < state.fromYear) state.setFromYear(to)
    },
    [state],
  )

  if (!state) return <div id="timeline-scrubber" />

  const ticks: number[] = []
  for (let y = state.minYear; y <= state.maxYear; y++) {
    ticks.push(y)
  }

  return (
    <div id="timeline-scrubber">
      <label className="timeline-toggle">
        <input
          type="checkbox"
          id="timeline-enabled"
          checked={state.enabled}
          onChange={(e) => state.setEnabled(e.target.checked)}
        />
        Conflict Events
      </label>
      <div className="timeline-range">
        <span id="timeline-from-label">{state.fromYear}</span>
        <div className="timeline-sliders">
          <input
            type="range"
            id="timeline-from"
            min={state.minYear}
            max={state.maxYear}
            value={state.fromYear}
            onChange={handleFromChange}
          />
          <input
            type="range"
            id="timeline-to"
            min={state.minYear}
            max={state.maxYear}
            value={state.toYear}
            onChange={handleToChange}
          />
        </div>
        <span id="timeline-to-label">{state.toYear}</span>
      </div>
      <div id="timeline-ticks">
        {ticks.map((y) => (
          <span key={y}>{y}</span>
        ))}
      </div>
    </div>
  )
}
